type DateValue = Date | number | string | null | undefined;

const DEFAULT_TIME_ZONE = "Europe/Prague";
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

export type WatchDogDateRange = "today" | "7d" | "14d" | "21d" | "28d" | "all";

const WATCHDOG_RANGE_DAYS: Record<Exclude<WatchDogDateRange, "all">, number> = {
  today: 1,
  "7d": 7,
  "14d": 14,
  "21d": 21,
  "28d": 28,
};

const pragueDateFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: DEFAULT_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const getPragueCalendarDay = (date: Date) => {
  const parts = pragueDateFormatter.formatToParts(date);
  const year = Number(parts.find(({ type }) => type === "year")?.value);
  const month = Number(parts.find(({ type }) => type === "month")?.value);
  const day = Number(parts.find(({ type }) => type === "day")?.value);

  return Math.floor(Date.UTC(year, month - 1, day) / MILLISECONDS_PER_DAY);
};

const getPragueIsoWeek = (date: Date) => {
  const parts = pragueDateFormatter.formatToParts(date);
  const year = Number(parts.find(({ type }) => type === "year")?.value);
  const month = Number(parts.find(({ type }) => type === "month")?.value);
  const day = Number(parts.find(({ type }) => type === "day")?.value);
  const calendarDate = new Date(Date.UTC(year, month - 1, day));

  calendarDate.setUTCDate(
    calendarDate.getUTCDate() + 4 - (calendarDate.getUTCDay() || 7),
  );
  const isoYearStart = new Date(Date.UTC(calendarDate.getUTCFullYear(), 0, 1));

  const week = Math.ceil(
    ((calendarDate.getTime() - isoYearStart.getTime()) / MILLISECONDS_PER_DAY +
      1) /
      7,
  );

  return { year: calendarDate.getUTCFullYear(), week };
};

export const getPragueIsoWeekNumber = (date = new Date()) => {
  return getPragueIsoWeek(date).week;
};

export const getPragueIsoWeekKey = (date: Date) => {
  const { year, week } = getPragueIsoWeek(date);

  return `${year}-W${String(week).padStart(2, "0")}`;
};

export const isDateInWatchDogRange = (
  value: DateValue,
  range: WatchDogDateRange,
  now = new Date(),
) => {
  if (!value) return false;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime()) || date.getTime() > now.getTime()) {
    return false;
  }

  if (range === "all") {
    return true;
  }

  const today = getPragueCalendarDay(now);
  const offerDay = getPragueCalendarDay(date);
  const firstIncludedDay = today - (WATCHDOG_RANGE_DAYS[range] - 1);

  return offerDay >= firstIncludedDay && offerDay <= today;
};

export const formatCzDate = (
  value: DateValue,
  options?: Intl.DateTimeFormatOptions,
): string => {
  if (!value) return "";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("cs-CZ", {
    timeZone: DEFAULT_TIME_ZONE,
    ...options,
  }).format(date);
};

export const formatTime = (date: Date) => {
  return new Intl.DateTimeFormat("cs-CZ", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  }).format(date);
};

export const formatDateTime = (date: Date) => {
  return new Intl.DateTimeFormat("cs-CZ", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};
